import { useMemo, useRef } from 'react';
import { SupplierThread } from '../components/forms/outreach/types';
import { ActionIndex, sortThreadsByAction, threadIdentity } from './outreachActionModel';

/**
 * The product rule: information updates in place; rows don't move under the
 * cursor.
 *
 * `sortThreadsByAction` answers "what needs the buyer first", and that answer
 * changes the instant they act on a thread — the row they are reading would
 * sink and every row below it would shift while they are looking at it. So the
 * order is a SNAPSHOT: taken when the surface mounts, and held for as long as
 * the buyer stays on it. Badges, read lines, pills and counts keep updating
 * live in the rows where they already are.
 *
 * A genuinely new thread is the one thing that may join: it is placed where the
 * current sort would put it relative to the rows already on screen, which can
 * push rows down but never past each other. Leaving the step and coming back
 * re-mounts the hook and re-sorts from scratch.
 */
export const useStableThreadOrder = (threads: SupplierThread[], index: ActionIndex): SupplierThread[] => {
  const frozenOrder = useRef<string[]>([]);

  return useMemo(() => {
    const byIdentity = new Map(threads.map((thread) => [threadIdentity(thread), thread]));
    const placed = frozenOrder.current
      .filter((identity) => byIdentity.has(identity))
      .map((identity) => byIdentity.get(identity) as SupplierThread);
    const placedIdentities = new Set(placed.map(threadIdentity));

    const sorted = sortThreadsByAction(threads, index);
    const rankOf = new Map(sorted.map((thread, rank) => [threadIdentity(thread), rank]));
    sorted.forEach((thread) => {
      const identity = threadIdentity(thread);
      if (placedIdentities.has(identity)) return;
      const rank = rankOf.get(identity) as number;
      const at = placed.findIndex((row) => (rankOf.get(threadIdentity(row)) as number) > rank);
      placed.splice(at === -1 ? placed.length : at, 0, thread);
      placedIdentities.add(identity);
    });

    frozenOrder.current = placed.map(threadIdentity);
    return placed;
  }, [threads, index]);
};
